/**
 * ClickOutside Helper component
 * Detect when a click has taken place outside of a componebt
 * @usage
 * ```
 * function handleClickOutside() {
 *   doSomething();
 * }
 * <ClickOutside callback={handleClickOutside}>
 *   <ComponentName />
 * </ClickOutside>
 *
 * ```
 */
import React, { useRef, useEffect } from "react"
import PropTypes from "prop-types"

function useClickOutside(ref, callback) {
  /**
   * Alert if clicked on outside of element
   */
  function handleClickOutside(event) {
    if (ref.current && !ref.current.contains(event.target)) {
      callback()
    }
  }

  useEffect(() => {
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside)
    }
  })
}

/**
 * Component that alerts if you click outside of it
 */
function ClickOutside(props) {
  const wrapperRef = useRef(null)
  useClickOutside(wrapperRef, props.callback)

  return (
    <div className={props.className} ref={wrapperRef}>
      {props.children}
    </div>
  )
}

ClickOutside.defaultProps = {
  className: undefined,
}

ClickOutside.propTypes = {
  callback: PropTypes.func.isRequired,
  children: PropTypes.any.isRequired,
  className: PropTypes.string,
}

export default ClickOutside
